// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (customer reviews + testimonial curation: shared client for the two screens)
// GET /admin/reviews (delivered orders that carry a customer review + staff moderation) and PUT /admin/orders/{ref}/review-moderation (admin/editor),
// plus the small pure helpers both screens share (averages, eligibility, verbatim-quote check, star row).
// Moderation is a staff-only record: nothing here publishes or sends anything.
// Neighbours: pages/admin/AdminReviews, pages/admin/AdminTestimonials, utils/orders (Order/Review types + tag labels), utils/adminApi.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";
import { seafoodItems } from "../data/seafood";
import type { Order } from "./orders";

export type ReviewStatus = "new" | "approved" | "flagged" | "hidden";
export type TestimonialState = "none" | "shortlisted" | "archived";
export interface Moderation { status: ReviewStatus; testimonial: TestimonialState; quote: string | null; note: string | null; by: string | null; at: string | null }
export interface ReviewRow { order: Order; moderation: Moderation }
export type ModerationChange = Partial<{ status: ReviewStatus; testimonial: TestimonialState; quote: string; note: string }>;

export const fetchReviews = () => apiAuthed<ReviewRow[]>("GET", "/admin/reviews?limit=300");
export const moderateReview = (ref: string, change: ModerationChange) => apiAuthed<ReviewRow>("PUT", `/admin/orders/${encodeURIComponent(ref)}/review-moderation`, change);

export const MIN_TESTIMONIAL_COMMENT = 20;
export const MAX_QUOTE = 300;
export const catchName = (id: string) => seafoodItems.find((s) => s.id === id)?.name ?? id;
export const catchCategory = (id: string) => seafoodItems.find((s) => s.id === id)?.category ?? null;
export const initials = (name: string) => name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
export const reviewer = (row: ReviewRow) => row.order.delivery.name;
export const average = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);
export const fmt1 = (n: number | null, digits = 2) => (n === null ? "—" : n.toFixed(digits));

/** A rating band read straight from the stars (this is NOT language analysis). */
export function band(stars: number) {
  if (stars >= 4) return { label: "Positive", tone: "good" as const };
  if (stars === 3) return { label: "Neutral", tone: "mid" as const };
  return { label: "Negative", tone: "bad" as const };
}

/** A review is a testimonial candidate when it is 4-5 stars, says something real, and staff haven't flagged or hidden it. */
export function isCandidate(row: ReviewRow) {
  const r = row.order.review;
  return !!r && r.overall >= 4 && (r.comment ?? "").trim().length >= MIN_TESTIMONIAL_COMMENT && row.moderation.status !== "flagged" && row.moderation.status !== "hidden";
}

const squash = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();
/** A featured quote must be the customer's own words: a verbatim excerpt of their comment (whitespace and case aside). */
export const isVerbatim = (quote: string, comment: string | null) => squash(quote).length > 0 && squash(comment ?? "").includes(squash(quote).replace(/…$|\.\.\.$/, "").trim());

/** The rows needing a decision: not yet looked at and rated 1-2 stars, or flagged. */
export const needsAction = (row: ReviewRow) => row.moderation.status === "flagged" || (row.moderation.status === "new" && (row.order.review?.overall ?? 5) <= 2);
export const deliveryIssue = (row: ReviewRow) => { const d = row.order.review?.delivery; return !!d && (d.rating <= 2 || d.tags.includes("late") || d.tags.includes("packaging-damaged")); };

export const STATUS_TEXT: Record<ReviewStatus, string> = { new: "Awaiting review", approved: "Approved", flagged: "Flagged for ops", hidden: "Hidden" };
export const STATUS_CLASS: Record<ReviewStatus, string> = {
  new: "bg-ocean-700/10 dark:bg-white/10",
  approved: "bg-teal-500/15 text-teal-800 dark:text-teal-300",
  flagged: "bg-coral-500 text-white",
  hidden: "bg-ocean-900/10 line-through dark:bg-white/10",
};
export const TESTIMONIAL_TEXT: Record<TestimonialState, string> = { none: "Not shortlisted", shortlisted: "Shortlisted", archived: "Archived" };

